import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  SearchAccreditedEstablishmentMarkersArgs,
  SearchAccreditedEstablishmentMarkersResponse,
  searchEstablishmentMarkers
} from '@/modules/accredited-establishments/api'
import { addError } from '@/global/plugins/observability'
import {
  AccreditedEstablishmentsCode,
  Context
} from '@/global/plugins/observability/observability.constants'
import { makeSearchEstablishmentMarkersQueryKey } from '@/modules/accredited-establishments/functions'
import { AxiosError } from 'axios'
import { toastApiError } from '@/global/utils/toastApiError/toastApiError'
import { ApiErrorResponse } from '@/global/api/types'

export type UseSearchEstablishmentMarkers =
  SearchAccreditedEstablishmentMarkersArgs & {
    enabled?: boolean
    toastOnError?: boolean
  }

export const useSearchEstablishmentMarkers = ({
  enabled,
  toastOnError = true,
  ...rest
}: UseSearchEstablishmentMarkers) => {
  const result = useQuery<
    SearchAccreditedEstablishmentMarkersResponse,
    AxiosError<ApiErrorResponse>
  >({
    queryKey: makeSearchEstablishmentMarkersQueryKey(rest),
    queryFn: () => searchEstablishmentMarkers(rest),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    select: data => {
      return {
        ...data,
        items: data.items.map(item => ({
          ...item,
          icon:
            item.icon &&
            item.icon.startsWith(
              'https://s3.amazonaws.com/web.assets.alice.com.br/'
            )
              ? item.icon
              : undefined
        }))
      }
    }
  })

  const { error } = result

  useEffect(() => {
    if (error && toastOnError) {
      const data = {
        context: Context.ACCREDITED_ESTABLISHMENTS,
        code: AccreditedEstablishmentsCode.GET_ACCREDITED_ESTABLISHMENT_MARKERS_ERROR,
        message:
          'Erro ao obter os pins dos estabelecimentos com os filtros selecionados',
        ...rest
      }
      addError({ error, data })

      toastApiError({ status: error.response?.status })
    }
  }, [error, rest])

  return result
}
